import { useQuery } from '@tanstack/react-query';
import { Building2, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import type { CustomerItem, Paginated, Ref } from '@/lib/types';

/** Suchfeld mit Vorschlagsliste für Kunden. */
export function CustomerPicker({ value, onChange, id }: { value: Ref | null; onChange: (c: Ref | null) => void; id?: string }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 200);
    return () => clearTimeout(t);
  }, [term]);

  const { data } = useQuery({
    queryKey: ['customers', 'pick', debounced],
    queryFn: () => api<Paginated<CustomerItem>>(`/customers?pageSize=8&q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length > 0,
  });

  if (value) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border bg-card px-3 text-sm">
        <span className="truncate">{value.name}</span>
        <button type="button" onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground" aria-label="Kunde entfernen">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        id={id}
        placeholder="Kunde suchen (Name, Nummer, E-Mail)"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && debounced && data && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-lg">
          {data.items.length === 0 && <li className="px-2 py-2 text-sm text-muted-foreground">Keine Treffer</li>}
          {data.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange({ id: c.id, name: c.name });
                  setTerm('');
                  setOpen(false);
                }}
              >
                {c.type === 'BUSINESS' ? <Building2 className="size-4 text-muted-foreground" /> : <User className="size-4 text-muted-foreground" />}
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{c.number}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
